import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useAllOrders } from '../context/AllOrdersContext';
import { useGeolocation } from '../hooks/useGeolocation';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../firebase/client';
import { AppContext } from '../context/AppContext';
import { DeliveryIcon } from '../components/icons';
import NotificationService from '../services/NotificationService';
import { useNotifications } from '../context/NotificationContext';
import { useData } from '../context/DataContext';
import { calculateDistance, formatDistance } from '../utils/geoUtils';
import { formatDateReadable } from '../utils/dateUtils';


const LivreurHomeScreen: React.FC = () => {
    const { currentUser, updateUserProfile } = useAuth();
    const { placedOrders, loading: ordersLoading, updateOrderStatus } = useAllOrders();
    const { stopAlarm } = useNotifications();
    const { restaurants } = useData();
    const { requestPermissions, getCurrentPosition, errorCode, isTracking, lastSyncTimestamp, isOnline: geoIsOnline } = useGeolocation(currentUser?.uid);
    const { setView } = React.useContext(AppContext);

    const [updatingStatus, setUpdatingStatus] = useState(false);
    const [loadingOrderId, setLoadingOrderId] = useState<string | null>(null);


    const isOnline = currentUser?.isOnline || false;

    const handleToggleOnline = async () => {
        if (!currentUser) return;
        setUpdatingStatus(true);
        try {
            const newStatus = !isOnline;

            if (newStatus) {
                // Forcing location check before going online
                const hasPermission = await requestPermissions();
                if (!hasPermission) {
                    alert("La localisation est indispensable pour vous mettre en ligne et recevoir des missions.");
                    setUpdatingStatus(false);
                    return;
                }
                
                const pos = await getCurrentPosition();
                if (!pos) {
                    // Check if it's just a signal/timeout issue (Code 2 or 3)
                    // If permissions are granted, we can allow going online even if signal is weak
                    // The background tracker will catch up later.
                    console.warn("Initial location check failed, but permissions are granted. Proceeding with warning.");
                    alert("Signal GPS faible. Vous êtes en ligne, mais votre position se mettra à jour dès que le signal sera meilleur.");
                }
            }

            // Tracking is now handled globally in AllOrdersContext based on isOnline and role
            await updateUserProfile({ isOnline: newStatus });
        } catch (error) {
            console.error("Toggle online error:", error);
            alert('Erreur lors du changement de statut');
        } finally {
            setUpdatingStatus(false);
        }
    };


    const handleAcceptOrder = async (orderId: string) => {
        if (loadingOrderId) return;
        setLoadingOrderId(orderId);
        try {
            const order = placedOrders.find(o => o.id === orderId);
            if (!order || !currentUser) {
                setLoadingOrderId(null);
                return;
            }

            const driverData: any = {
                id: currentUser.uid,
                name: currentUser.name,
                phone: currentUser.phone || '',
                location: currentUser.location,
                rating: currentUser.rating,
                ratingCount: currentUser.ratingCount,
                fcmToken: currentUser.fcmToken,
                isOnline: currentUser.isOnline,
                isManual: true
            };

            // On attend la confirmation pour être sûr que c'est bien enregistré
            // Ajout d'un délai de sécurité pour débloquer l'UI quoi qu'il arrive
            const timeoutId = setTimeout(() => {
                setLoadingOrderId(null);
            }, 15000);

            await updateOrderStatus(orderId, order.status, driverData);
            clearTimeout(timeoutId);
        } catch (error) {
            console.error('Error accepting order:', error);
            alert('Impossible d\'accepter la commande. Veuillez vérifier votre connexion.');
        } finally {
            setLoadingOrderId(null);
        }
    };

    const handleRejectOrder = async (orderId: string) => {
        console.log(`[DEBUG] handleRejectOrder called for orderId: ${orderId}`);
        if (loadingOrderId) {
            console.log('[DEBUG] handleRejectOrder: already loading another order');
            return;
        }
        
        // Temporarily bypassing window.confirm for debugging native issues
        // if (!window.confirm("Refuser cette mission ?")) return;
        
        setLoadingOrderId(orderId);
        try {
            const order = placedOrders.find(o => o.id === orderId);
            if (!order || !currentUser) {
                console.log('[DEBUG] handleRejectOrder: order or currentUser missing', { found: !!order, user: !!currentUser });
                return;
            }

            const driverData = {
                id: currentUser.uid,
                name: currentUser.name,
                phone: currentUser.phone || '',
                location: currentUser.location,
                rating: currentUser.rating,
                ratingCount: currentUser.ratingCount,
                fcmToken: currentUser.fcmToken,
                isOnline: currentUser.isOnline,
                isManual: true
            } as any;

            console.log('[DEBUG] handleRejectOrder: calling updateOrderStatus with rejected override');
            await updateOrderStatus(orderId, order.status, driverData, undefined, 'rejected');
            console.log('[DEBUG] handleRejectOrder: updateOrderStatus success');
        } catch (error) {
            console.error('Error rejecting order:', error);
            alert('Erreur lors du refus de la mission.');
        } finally {
            setLoadingOrderId(null);
        }
    };

    const pendingOrders = placedOrders.filter(o => {
        // Only show missions explicitly assigned to ME that are still pending acceptance
        return (o.livreurId === currentUser?.uid || o.assignedDriver?.id === currentUser?.uid) && o.livreurStatus === 'pending';
    });

    // Alarm persists until pendingOrders is empty (accepted or rejected)
    useEffect(() => {
        if (pendingOrders.length === 0) {
            stopAlarm();
        }
    }, [pendingOrders.length, stopAlarm]);

    const activeOrders = placedOrders.filter(
        o => (o.livreurId === currentUser?.uid || o.assignedDriver?.id === currentUser?.uid) && o.livreurStatus === 'accepted'
    );

    return (
        <div className="min-h-screen bg-gray-50 pb-20">
            {/* Header */}
            <div className="bg-white shadow-sm p-4 sticky top-0 z-10 flex justify-between items-center">
                <div>
                    <h1 className="text-xl font-black text-gray-900 tracking-tighter uppercase italic flex items-center gap-2">
                        <DeliveryIcon className="w-6 h-6 text-red-600" />
                        Livreur
                    </h1>
                    <p className="text-gray-500 text-sm">Bonjour, {currentUser?.name}</p>
                </div>
                <div className="flex items-center gap-2">
                    <span className={`text-sm font-medium ${isOnline ? 'text-green-600' : 'text-gray-500'}`}>
                        {isOnline ? 'En ligne' : 'Hors ligne'}
                    </span>
                    <button
                        onClick={handleToggleOnline}
                        disabled={updatingStatus}
                        className={`w-12 h-6 rounded-full transition-colors relative ${isOnline ? 'bg-green-500' : 'bg-gray-300'} ${updatingStatus ? 'opacity-50' : ''}`}
                    >
                        <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform ${isOnline ? 'translate-x-6' : 'translate-x-0.5'}`} />
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <div className="p-4 space-y-6">
                
                {/* GPS Diagnostic Widget */}
                {isOnline && (
                    <div className="space-y-3">
                        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className={`w-3 h-3 rounded-full ${isTracking ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`} />
                                <div>
                                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none mb-1">Status GPS</p>
                                    <p className="text-xs font-bold text-gray-800 uppercase italic">
                                        {isTracking ? "Suivi en cours" : "GPS Inactif"}
                                    </p>
                                </div>
                            </div>
                            <div className="text-right">
                                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none mb-1">
                                    {geoIsOnline ? "Connecté" : "Hors-ligne 🔴"}
                                </p>
                                <p className="text-[9px] font-bold text-gray-400">
                                    {lastSyncTimestamp ? `Dernière synchro : ${Math.round((Date.now() - lastSyncTimestamp) / 1000)}s` : "En attente..."}
                                </p>
                            </div>
                        </div>

                        {/* Android Optimization Warning */}
                        {Capacitor.getPlatform() === 'android' && (
                            <div className="bg-orange-50 rounded-2xl p-3 border border-orange-100 flex items-start gap-3">
                                <span className="text-orange-500 mt-0.5">⚠️</span>
                                <p className="text-[9px] font-bold text-orange-800 leading-tight uppercase tracking-tight">
                                    <span className="font-black underline">IMPORTANT :</span> Pour un suivi précis sur long trajet, désactivez "l'optimisation de batterie" pour Yumii dans vos réglages téléphone.
                                </p>
                            </div>
                        )}
                    </div>
                )}

                {/* Pending Assignments */}
                <section>
                    <h2 className="text-xl font-black text-gray-900 mb-4 tracking-tighter uppercase italic flex items-center gap-2">
                        <span className="bg-red-100 text-red-600 w-6 h-6 rounded-full flex items-center justify-center text-sm not-italic">
                            {pendingOrders.length}
                        </span>
                        Nouvelles <span className="text-red-600 ml-1">missions</span>
                    </h2>

                    {ordersLoading ? (
                        <div className="text-center py-10 space-y-3">
                            <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
                            <p className="text-gray-400 text-sm font-medium animate-pulse">Récupération des missions en cours...</p>
                        </div>
                    ) : pendingOrders.length === 0 ? (
                        <div className="py-12 flex flex-col items-center justify-center bg-white rounded-2xl border border-dashed border-gray-200 animate-fade-in shadow-sm">
                            <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4 relative">
                                {isOnline && <div className="absolute inset-0 bg-green-100 rounded-full animate-ping opacity-20"></div>}
                                <span className={`text-4xl transition-all ${isOnline ? 'scale-110' : 'grayscale opacity-30'}`}>
                                    {isOnline ? '📡' : '💤'}
                                </span>
                            </div>
                            <h3 className="text-lg font-black text-gray-900 tracking-tighter uppercase italic mb-1">
                                {isOnline ? "En Recherche..." : "Passer en ligne"}
                            </h3>
                            <p className="text-gray-400 font-bold text-[10px] uppercase tracking-widest italic px-10 text-center leading-relaxed">
                                {isOnline 
                                    ? "Nous recherchons des missions disponibles autour de vous." 
                                    : "Vous devez être en ligne pour recevoir de nouvelles notifications de livraison."}
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {pendingOrders.map(order => (
                                <div key={order.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                                    <div className="p-4 border-b border-gray-50">
                                        <div className="flex justify-between items-start mb-4">
                                            <h3 className="font-bold text-lg">{order.restaurantName}</h3>
                                            <div className="text-right">
                                                <span className="block font-black text-xl text-red-600 leading-none">{order.deliveryFee} DA</span>
                                                <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Gain estimé</span>
                                                <p className="text-[9px] text-gray-400 mt-1">{formatDateReadable(order.timestamp)}</p>
                                            </div>
                                        </div>

                                        {(() => {
                                            const resto = restaurants.find(r => r.id === order.restaurantId);
                                            if (!currentUser?.location || !resto?.location || !order.customerLocation) return null;

                                            const distToResto = calculateDistance(
                                                currentUser.location.lat,
                                                currentUser.location.lng,
                                                resto.location.lat,
                                                resto.location.lng
                                            );
                                            const distToClient = calculateDistance(
                                                resto.location.lat,
                                                resto.location.lng,
                                                order.customerLocation.lat,
                                                order.customerLocation.lng
                                            );
                                            const totalDist = distToResto + distToClient;

                                            return (
                                                <div className="mb-4">
                                                    <div className="bg-red-50 p-3 rounded-2xl mb-3 flex items-center justify-between border border-red-100">
                                                        <div>
                                                            <p className="text-[10px] font-black text-red-400 uppercase tracking-[0.2em] mb-0.5">Distance Totale</p>
                                                            <p className="text-lg font-black text-red-700 leading-none">{formatDistance(totalDist)}</p>
                                                        </div>
                                                        <button 
                                                            onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&origin=${currentUser.location!.lat},${currentUser.location!.lng}&destination=${order.customerLocation!.lat},${order.customerLocation!.lng}&waypoints=${resto.location!.lat},${resto.location!.lng}`, '_blank')}
                                                            className="bg-white text-red-600 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-sm border border-red-200 active:scale-95 transition-all flex items-center gap-2"
                                                        >
                                                            <span>📍</span> Itinéraire
                                                        </button>
                                                    </div>

                                                    <div className="space-y-3 px-1">
                                                        <div className="flex items-start gap-3 relative">
                                                            <div className="absolute left-[11px] top-6 bottom-0 w-0.5 bg-gray-100 border-l border-dashed border-gray-300"></div>
                                                            <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-[10px] shrink-0 z-10">🏠</div>
                                                            <div className="pt-0.5">
                                                                <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest leading-none mb-1">Position Actuelle</p>
                                                                <p className="text-xs font-bold text-gray-800">C'est vous</p>
                                                            </div>
                                                        </div>

                                                        <div className="flex items-start gap-3 relative">
                                                            <div className="absolute left-[11px] top-6 bottom-0 w-0.5 bg-gray-100 border-l border-dashed border-gray-300"></div>
                                                            <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center text-[10px] shrink-0 z-10 text-red-600 font-black">1</div>
                                                            <div className="pt-0.5 flex-1">
                                                                <div className="flex justify-between items-center mb-1">
                                                                    <p className="text-[10px] text-red-400 font-black uppercase tracking-widest leading-none">Récupération</p>
                                                                    <span className="text-[10px] bg-red-50 text-red-600 px-2 py-0.5 rounded-full font-black">+{formatDistance(distToResto)}</span>
                                                                </div>
                                                                <p className="text-xs font-bold text-gray-800">{resto.name}</p>
                                                            </div>
                                                        </div>

                                                        <div className="flex items-start gap-3 relative">
                                                            <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center text-[10px] shrink-0 z-10 text-green-600 font-black">2</div>
                                                            <div className="pt-0.5 flex-1">
                                                                <div className="flex justify-between items-center mb-1">
                                                                    <p className="text-[10px] text-green-400 font-black uppercase tracking-widest leading-none">Livraison</p>
                                                                    <span className="text-[10px] bg-green-50 text-green-600 px-2 py-0.5 rounded-full font-black">+{formatDistance(distToClient)}</span>
                                                                </div>
                                                                <p className="text-xs font-bold text-gray-800">Vers {order.customerName}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })()}

                                        <p className="text-[8px] text-gray-300 font-bold uppercase tracking-widest text-center">
                                            RÉF: {order.id.slice(4, 12)}
                                        </p>
                                    </div>
                                    <div className="flex bg-gray-50 p-3 gap-3">
                                        <button
                                            onClick={() => handleRejectOrder(order.id)}
                                            disabled={!!loadingOrderId}
                                            className={`flex-1 py-2.5 text-red-600 font-bold bg-white border border-red-100 rounded-xl shadow-sm active:scale-95 transition-all ${loadingOrderId === order.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        >
                                            Refuser
                                        </button>
                                        <button
                                            onClick={() => handleAcceptOrder(order.id)}
                                            disabled={!!loadingOrderId}
                                            className={`flex-[2] py-2.5 text-white font-bold bg-red-600 rounded-xl shadow-md hover:bg-red-700 active:scale-95 transition-all ${loadingOrderId === order.id ? 'opacity-70 cursor-not-allowed' : 'shadow-red-200'}`}
                                        >
                                            {loadingOrderId === order.id ? 'Patientez...' : 'Accepter'}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </section>

                {/* Active Deliveries */}
                <section>
                    <h2 className="text-xl font-black text-gray-900 mb-4 tracking-tighter uppercase italic flex items-center gap-2">
                        <span className="bg-gray-200 text-gray-700 w-6 h-6 rounded-full flex items-center justify-center text-sm not-italic">
                            {activeOrders.length}
                        </span>
                        En <span className="text-red-600 ml-1">cours</span>
                    </h2>

                    {activeOrders.length === 0 && !ordersLoading && (
                        <div className="text-center p-6 text-gray-500">
                            Aucune livraison en cours
                        </div>
                    )}

                    <div className="space-y-4">
                        {activeOrders.map(order => (
                            <div
                                key={order.id}
                                onClick={() => setView({ screen: 'livreurActiveOrder', params: { orderId: order.id } })}
                                className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 cursor-pointer hover:border-red-300 transition-colors"
                            >
                                <div className="flex justify-between items-start mb-2">
                                    <h3 className="font-bold text-lg">{order.restaurantName}</h3>
                                    <span className="text-xs font-semibold px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full">
                                        {order.status}
                                    </span>
                                </div>
                                <p className="text-sm text-gray-600 mb-2">
                                    Livraison vers: <span className="font-medium text-gray-800">{order.customerName}</span>
                                </p>
                                <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-50">
                                    <span className="text-sm text-gray-500">{order.items.reduce((acc, item) => acc + item.quantity, 0)} articles</span>
                                    <div className="flex items-center text-red-600 text-sm font-semibold gap-1">
                                        Détails
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* --- DIAGNOSTIC DASHBOARD (Internal Dev) --- */}
                <div className="mt-8 pt-6 border-t border-gray-100 pb-10">
                    <div className="bg-gray-50 rounded-lg p-3 text-[10px] text-gray-400 font-mono space-y-1">
                        <div className="flex justify-between">
                            <span>UID:</span>
                            <span>{currentUser?.uid?.substring(0, 8)}...</span>
                        </div>
                        <div className="flex justify-between font-bold">
                            <span>WILAYA:</span>
                            <span className={currentUser?.wilaya ? "text-green-600" : "text-red-600"}>
                                {currentUser?.wilaya || "NON DÉFINIE"}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span>STATUS:</span>
                            <span className={isOnline ? "text-green-600 font-bold" : "text-gray-400"}>
                                {isOnline ? "EN LIGNE" : "HORS LIGNE"}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span>ORDERS FETCHED:</span>
                            <span>{placedOrders.length}</span>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default LivreurHomeScreen;
